<template>
  <v-container fluid>
    <v-row align="center" class="mb-2">
      <v-col cols="12" md="3">
        <h2 class="text-h5">Gate report</h2>
        <div class="caption grey--text">Vehicle movements recorded at the factory gate</div>
      </v-col>
      <v-col cols="6" md="2">
        <v-text-field v-model="from" label="From" type="date" dense outlined hide-details />
      </v-col>
      <v-col cols="6" md="2">
        <v-text-field v-model="to" label="To" type="date" dense outlined hide-details />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field v-model="search" label="Search plate, company, driver" dense outlined
                      hide-details clearable prepend-inner-icon="mdi-magnify" />
      </v-col>
      <v-col cols="12" md="3">
        <v-btn color="primary" :loading="loading" @click="load">
          <v-icon left>mdi-magnify</v-icon>Show
        </v-btn>
        <v-btn text class="ml-2" @click="setToday">Today</v-btn>
        <v-btn text @click="setDays(7)">7 days</v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <!-- Headline numbers. The failures sit beside the traffic on purpose: a report
         showing only volume looks healthy while the gate is unmanned. -->
    <v-row v-if="summary" class="mb-2">
      <v-col v-for="t in tiles" :key="t.key" cols="6" md="2">
        <!-- A number nobody can open is a dead end: "8 not registered" is only
             useful if you can see WHICH eight. Each tile filters the table. -->
        <v-card
          outlined
          class="tile"
          :class="{ on: filter === t.key }"
          @click="pickTile(t)"
        >
          <v-card-text class="py-3">
            <div class="text-h4" :class="t.colour">{{ t.value }}</div>
            <div class="caption grey--text">{{ t.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="filter" class="mb-2" no-gutters>
      <v-col>
        <v-chip close color="primary" outlined @click:close="filter = ''">
          Showing {{ activeTileLabel.toLowerCase() }} ({{ shownVisits.length }})
        </v-chip>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="shownVisits"
        :search="search"
        :loading="loading"
        :items-per-page="25"
        dense
        :no-data-text="filter ? 'Nothing matches this tile' : 'No vehicles in this period'"
        @click:row="open"
        class="clickable-rows"
      >
        <template v-slot:item.visitType="{ item }">
          <span v-if="item.visitType">{{ typeLabel(item.visitType) }}</span>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.companyName="{ item }">
          <span v-if="item.companyName">
            {{ item.companyName }}
            <!-- from the vehicle registry, not a registration: plates are duplicated
                 across companies so this is a guess, and says so -->
            <v-icon v-if="item.companyFromRegistry" x-small color="grey"
                    title="from the vehicle registry — not confirmed">mdi-help-circle-outline</v-icon>
          </span>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.driverName="{ item }">
          <span v-if="item.driverName">{{ item.driverName }}</span>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.entryTime="{ item }">{{ fmt(item.entryTime) }}</template>
        <template v-slot:item.exitTime="{ item }">
          <span v-if="item.exitTime">{{ fmt(item.exitTime) }}</span>
          <v-chip v-else x-small color="blue lighten-4">still on site</v-chip>
        </template>
        <template v-slot:item.registered="{ item }">
          <v-chip x-small :color="item.registered ? 'green lighten-4' : 'red lighten-4'">
            {{ item.registered ? 'registered' : 'not registered' }}
          </v-chip>
        </template>
        <template v-slot:item.onSite="{ item }">{{ duration(item) }}</template>
      </v-data-table>
    </v-card>

    <!-- One vehicle in full. The photos are the point: everything else can be read
         off the table, but "what did it look like" cannot. -->
    <v-dialog v-model="dialog" max-width="900" scrollable>
      <v-card v-if="selected">
        <v-card-title>
          <span class="text-h6">{{ selected.plateNumber }}</span>
          <v-chip x-small class="ml-3"
                  :color="selected.registered ? 'green lighten-4' : 'red lighten-4'">
            {{ selected.registered ? 'registered' : 'not registered' }}
          </v-chip>
          <v-spacer />
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col v-for="f in detailFields" :key="f.label" cols="6" md="4">
              <div class="caption grey--text">{{ f.label }}</div>
              <div>{{ f.value || '—' }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />
          <div class="subtitle-2 mb-2">
            Photos<span v-if="photos.length" class="grey--text"> ({{ photos.length }})</span>
          </div>
          <v-progress-linear v-if="detailLoading" indeterminate />
          <div v-else-if="!photos.length" class="grey--text">No photos on this visit</div>
          <v-row v-else dense>
            <v-col v-for="p in photos" :key="p.fileId" cols="6" md="4">
              <v-card outlined>
                <v-img v-if="photoUrls[p.fileId]" :src="photoUrls[p.fileId]" aspect-ratio="1.4" />
                <div v-else class="pa-6 text-center grey--text">…</div>
                <v-card-subtitle class="py-1 caption">{{ slotLabel(p.slot) }}</v-card-subtitle>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";

export default {
  name: "GateReportIndex",
  data() {
    return {
      from: this.isoToday(),
      to: this.isoToday(),
      summary: null,
      visits: [],
      search: "",
      filter: "",
      selected: null,
      photos: [],
      photoUrls: {},
      detailLoading: false,
      dialog: false,
      loading: false,
      error: "",
      headers: [
        { text: "Plate", value: "plateNumber" },
        { text: "Type", value: "visitType" },
        { text: "Company", value: "companyName" },
        { text: "Driver", value: "driverName" },
        { text: "In", value: "entryTime" },
        { text: "Out", value: "exitTime" },
        { text: "On site", value: "onSite", sortable: false },
        { text: "Registered", value: "registered" },
        { text: "Source", value: "entrySource" },
      ],
    };
  },
  computed: {
    detailFields() {
      const s = this.selected || {};
      return [
        { label: "Type", value: s.visitType ? this.typeLabel(s.visitType) : null },
        { label: "Company", value: s.companyName },
        { label: "Driver", value: s.driverName },
        { label: "Phone", value: s.phone },
        { label: "Goods", value: s.goodsDescription },
        { label: "Arrived", value: this.fmt(s.entryTime) },
        { label: "Left", value: s.exitTime ? this.fmt(s.exitTime) : "still on site" },
        { label: "On site", value: this.duration(s) },
        { label: "Source", value: s.entrySource },
        { label: "Left with goods", value: s.goodsOut ? "yes" : "no" },
        { label: "Document no.", value: s.goodsDocNo },
        { label: "Exit note", value: s.exitNote },
        { label: "Checked out by", value: s.exitBy },
        { label: "Worksheet", value: s.worksheetId },
      ];
    },
    // Counted here rather than server-side: the rows already carry exitBy, and
    // the table holds the whole period, so this needs no API change.
    autoClosedCount() {
      return this.visits.filter(this.isAutoClosed).length;
    },
    shownVisits() {
      const t = this.tiles.find((x) => x.key === this.filter);
      if (!t || !t.test) return this.visits;
      return this.visits.filter(t.test);
    },
    activeTileLabel() {
      const t = this.tiles.find((x) => x.key === this.filter);
      return t ? t.label : "";
    },
    tiles() {
      const s = this.summary || {};
      // `key` names the subset each tile opens; `test` is how a row is judged to
      // belong to it. Duplicates are not deleted, they are closed with a marker
      // in exitBy, which is why that is what we match on.
      return [
        { key: "", label: "Readings", value: s.visits || 0, colour: "", test: null },
        // The plate-reading error rate, made visible. One lorry produced four
        // readings in three minutes; without a number on it the problem gets
        // tidied away rather than fixed.
        { key: "duplicate", label: "Duplicate readings", value: s.duplicateReadings || 0,
          colour: (s.duplicateReadings ? "orange--text" : ""), test: this.isDuplicate },
        { key: "actual", label: "Actual vehicles", value: s.distinctVehicles != null ? s.distinctVehicles : (s.visits || 0), colour: "",
          test: (v) => !this.isDuplicate(v) },
        { key: "unregistered", label: "Not registered", value: s.unregistered || 0, colour: (s.unregistered ? "red--text" : ""),
          test: (v) => !v.registered },
        { key: "unconfirmed", label: "Never confirmed", value: s.unacknowledged || 0, colour: (s.unacknowledged ? "orange--text" : ""),
          test: (v) => !v.acknowledgedAt },
        { key: "onsite", label: "Still on site", value: s.stillOnSite || 0, colour: (s.stillOnSite ? "orange--text" : ""),
          test: (v) => !v.exitTime },
        // The failure that used to hide itself. Auto-close stamps an exit time
        // after 12h with no photos and no inspection, so the visit stopped
        // counting as "still on site" and started looking like a completed one.
        // A vehicle nobody checked out must never read the same as one a guard
        // signed off, so it gets its own number, in red.
        { key: "unchecked", label: "Left unchecked", value: this.autoClosedCount,
          colour: (this.autoClosedCount ? "red--text" : ""), test: this.isAutoClosed },
        { key: "goods", label: "Left with goods", value: s.leftWithGoods || 0, colour: "",
          test: (v) => !!v.goodsOut },
        { key: "nodoc", label: "Goods, no document", value: s.goodsWithoutDocument || 0, colour: (s.goodsWithoutDocument ? "red--text" : ""),
          test: (v) => !!v.goodsOut && !v.goodsDocNo },
      ];
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    Object.values(this.photoUrls).forEach((u) => URL.revokeObjectURL(u));
  },
  watch: {
    dialog(open) {
      if (!open) {
        Object.values(this.photoUrls).forEach((u) => URL.revokeObjectURL(u));
        this.photoUrls = {};
      }
    },
  },
  methods: {
    // A duplicate is a second reading of one vehicle: closed, never deleted, so
    // the plate-reading error rate stays measurable.
    isAutoClosed(v) {
      return !!v.exitBy && v.exitBy.indexOf("auto-closed") === 0;
    },
    isDuplicate(v) {
      return !!v.exitBy && v.exitBy.indexOf("duplicate reading") === 0;
    },
    // Clicking the active tile again clears it — no separate "show all" button.
    pickTile(t) {
      this.filter = this.filter === t.key ? "" : t.key;
    },
    typeLabel(t) {
      const map = { DELIVERY: "Delivery", PICKUP: "Pick up", VISITOR: "Visitor",
                    CONTRACTOR: "Contractor", OTHER: "Other" };
      return map[t] || t;
    },
    slotLabel(slot) {
      if (slot === "ENTRY") return "On arrival (camera)";
      const map = { CARGO: "Cargo", CAB_DRIVER: "Cab / driver",
                    CAB_PASSENGER: "Cab / passenger", TOOLBOX: "Toolbox" };
      return map[slot] || slot;
    },
    // Photos need the auth token, which an <img src> cannot send, so fetch them
    // and hand the tag an object URL — the same approach the gate app uses.
    async open(item) {
      this.selected = item;
      this.dialog = true;
      this.photos = [];
      this.photoUrls = {};
      this.detailLoading = true;
      try {
        const r = await Vue.axios.get(`/api/gate/visit/${item.id}/photos`);
        this.photos = r.data || [];
        for (const p of this.photos) {
          try {
            const img = await Vue.axios.get(`/api/gate/photo/${p.fileId}`, { responseType: "blob" });
            this.$set(this.photoUrls, p.fileId, URL.createObjectURL(img.data));
          } catch (e) {
            /* one bad photo must not empty the whole dialog */
          }
        }
      } catch (e) {
        this.photos = [];
      } finally {
        this.detailLoading = false;
      }
    },
    isoToday() {
      // local (Thai) day, not toISOString() which is UTC and rolls early
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    setToday() {
      this.from = this.to = this.isoToday();
      this.load();
    },
    setDays(n) {
      const d = new Date();
      d.setDate(d.getDate() - (n - 1));
      const p = (x) => String(x).padStart(2, "0");
      this.from = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
      this.to = this.isoToday();
      this.load();
    },
    // The gate API sends timestamps with no timezone marker and the services JVM
    // runs on UTC, so a bare new Date() reads them 7 hours early.
    asDate(value) {
      if (!value) return null;
      // Do NOT append "Z" to a naive timestamp. That was right while bml-services
      // ran on UTC, and became wrong the moment production was given
      // TZ=Asia/Bangkok: the API now sends Thai wall time, and treating it as UTC
      // shifted it a second time. A truck that arrived at 14:28 was shown in this
      // report as 21:28 - times in the future, on a report of what already
      // happened.
      //
      // Measured 2026-09-09, same visit from each environment:
      //     production  2026-09-09T08:03:21   Bangkok wall time
      //     staging     2026-09-09T01:03:20   the same instant in UTC
      //
      // So a naive value is taken at face value - browser-local, Bangkok here -
      // and anything carrying an explicit zone is still honoured. bml-gate had the
      // identical bug and the identical fix.
      //
      // Staging still runs UTC and will read 7 hours early until that container
      // gets TZ=Asia/Bangkok (Deploy Hub ticket cmttietxo052801s9ejjrow2s).
      return new Date(value);
    },
    fmt(value) {
      const d = this.asDate(value);
      if (!d || isNaN(d)) return "";
      const p = (n) => String(n).padStart(2, "0");
      return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    duration(item) {
      const a = this.asDate(item.entryTime);
      const b = this.asDate(item.exitTime) || new Date();
      if (!a || isNaN(a)) return "";
      const mins = Math.max(0, Math.round((b - a) / 60000));
      return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const params = { params: { from: this.from, to: this.to } };
        const [s, v] = await Promise.all([
          Vue.axios.get("/api/gate/report/summary", params),
          Vue.axios.get("/api/gate/report/visits", params),
        ]);
        this.summary = s.data;
        this.visits = v.data || [];
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          "Could not load the gate report.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.clickable-rows >>> tbody tr { cursor: pointer; }
.tile { cursor: pointer; transition: border-color 0.15s, background-color 0.15s; }
.tile:hover { background-color: rgba(0, 0, 0, 0.03); }
.tile.on { border-color: var(--v-primary-base); border-width: 2px; }
</style>
